import React from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import leeuw from '../../assets/leeuw.png';
import coq from '../../assets/coq.png';
import { belgianDates } from './dateFormat';

const iso = (d) => d.toISOString().slice(0, 10);

/** Presets → {from, to} relative to today. */
export function presetPeriod(preset) {
  const today = new Date();
  const to = iso(today);
  const from = new Date(today);
  switch (preset) {
    case 'today': return { from: to, to };
    case '7d': from.setDate(from.getDate() - 6); return { from: iso(from), to };
    case '12m': from.setDate(from.getDate() - 364); return { from: iso(from), to };
    case '30d':
    default: from.setDate(from.getDate() - 29); return { from: iso(from), to };
  }
}

/**
 * Period + region picker shared by all statistics tabs.
 * value: {preset: 'today'|'7d'|'30d'|'12m'|'custom', from, to, region: ''|'VL'|'WA'}
 * Region = the SoftTouch entity of the client (lic_clients.softtouch_id): Vlaanderen or Wallonië.
 */
export default function StatsPeriodPicker({ value, onChange, disabled, t, generatedAt, lockedRegion = '' }) {
  const tr = (k, fallback) => (t ? t(k) : null) || fallback;
  const presets = [
    ['today', tr('stats_period_today', 'Vandaag')],
    ['7d', tr('stats_period_7d', '7 dagen')],
    ['30d', tr('stats_period_30d', '30 dagen')],
    ['12m', tr('stats_period_12m', '12 maanden')],
    ['custom', tr('stats_period_custom', 'Aangepast')],
  ];
  const regions = [
    ['', tr('stats_region_all', 'Alle regio\'s'), null],
    ['VL', tr('stats_region_vl', 'Vlaanderen'), leeuw],
    ['WA', tr('stats_region_wa', 'Wallonië'), coq],
  ];

  const choosePreset = (event, preset) => {
    if (preset === null) return;
    if (preset === 'custom') {
      onChange({ ...value, preset });
    } else {
      onChange({ ...value, preset, ...presetPeriod(preset) });
    }
  };

  const chooseRegion = (event, region) => {
    if (region === null) return;
    onChange({ ...value, region });
  };

  const changeDate = (field) => (event) => {
    onChange({ ...value, preset: 'custom', [field]: event.target.value });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '6px 0 10px' }}>
      <ToggleButtonGroup size="small" exclusive value={value.preset} onChange={choosePreset} aria-label="period">
        {presets.map(([key, label]) => <ToggleButton key={key} value={key} disabled={disabled}>{label}</ToggleButton>)}
      </ToggleButtonGroup>
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
        <input type="date" value={value.from} max={value.to} onChange={changeDate('from')} disabled={disabled} />
        <span>–</span>
        <input type="date" value={value.to} min={value.from} onChange={changeDate('to')} disabled={disabled} />
      </span>
      {lockedRegion ? (
        // Regional employees (type 2 / 3) always see their own region; no filter to choose from.
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: 0.85 }} title={regions.find((r) => r[0] === lockedRegion)?.[1]}>
          <img alt="" src={lockedRegion === 'WA' ? coq : leeuw} style={{ height: 20, width: 20 }} />
          {regions.find((r) => r[0] === lockedRegion)?.[1]}
        </span>
      ) : (
        <ToggleButtonGroup size="small" exclusive value={value.region || ''} onChange={chooseRegion} aria-label="region">
          {regions.map(([key, label, icon]) => (
            <ToggleButton key={key || 'all'} value={key} disabled={disabled} title={label} aria-label={label} style={{ gap: 6 }}>
              {icon && <img alt="" src={icon} style={{ height: 20, width: 20 }} />}
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      {generatedAt && <span style={{ fontSize: 12, opacity: 0.65 }}>{tr('stats_generated_at', 'Berekend om')} {belgianDates(generatedAt)}</span>}
    </div>
  );
}
